import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { DEFAULT_MAX_PER_REDEMPTION, LOYALTY_SCHEMA_VERSION } from '../config'
import {
  LoyaltyProgram,
  LoyaltyReward,
  LoyaltyRules,
  LoyaltyStats
} from '../types/loyalty.model'

interface LoyaltyState extends LoyaltyProgram {
  /** Filled by the API when there is one; empty until then. */
  stats: LoyaltyStats

  setIsActive: (value: boolean) => void
  setRules: (rules: Partial<LoyaltyRules>) => void
  addReward: (reward: Omit<LoyaltyReward, 'id'>) => void
  updateReward: (id: string, reward: Partial<Omit<LoyaltyReward, 'id'>>) => void
  deleteRewards: (ids: string[]) => void
  /** Replaces the whole document, for when the API starts hydrating this. */
  importProgram: (program: LoyaltyProgram) => void
}

/**
 * Defaults are a working programme rather than an empty one: a venue that turns
 * this on and changes nothing still has something coherent to show a guest.
 * One point per major unit is the rate people understand without being told.
 */
const DEFAULT_RULES: LoyaltyRules = {
  pointsPerMajorUnit: 1,
  minimumBillMinor: 2000,
  // Off by default. Every other default here describes a working programme;
  // this one describes a venue that has not decided to collect anything.
  contactBonusPoints: 0
}

// Not crypto, and not persisted anywhere that cares: an id only has to be unique
// within one restaurant's reward list. `crypto.randomUUID` is unavailable over
// plain http, which is how this app is served in development.
let sequence = 0
const nextId = () => {
  sequence += 1
  return `reward-${Date.now().toString(36)}-${sequence}`
}

export const useLoyaltyStore = create<LoyaltyState>()(
  persist(
    set => ({
      isActive: false,
      rules: DEFAULT_RULES,
      rewards: [],
      stats: {},

      setIsActive: value => set({ isActive: value }),

      setRules: rules =>
        set(state => ({ rules: { ...state.rules, ...rules } })),

      addReward: reward =>
        set(state => ({
          rewards: [...state.rewards, { ...reward, id: nextId() }]
        })),

      updateReward: (id, reward) =>
        set(state => ({
          rewards: state.rewards.map(item =>
            item.id === id ? { ...item, ...reward } : item
          )
        })),

      deleteRewards: ids =>
        set(state => ({
          rewards: state.rewards.filter(item => !ids.includes(item.id))
        })),

      importProgram: program =>
        set({
          isActive: program.isActive,
          rules: program.rules,
          rewards: program.rewards
        })
    }),
    {
      name: 'loyalty-storage',
      version: LOYALTY_SCHEMA_VERSION,
      // The counters belong to the server. Persisting them would show
      // yesterday's numbers as today's the moment an API exists.
      partialize: state => ({
        isActive: state.isActive,
        rules: state.rules,
        rewards: state.rewards
      }),
      /*
        v1 -> v2 dropped `rules.pointsValidityDays`.
        v2 -> v3 added `rules.contactBonusPoints`.
        v3 -> v4 added `rewards[].maxPerRedemption`.

        This has to exist even though the changes are small: a bumped `version`
        with no `migrate` makes zustand discard the entire payload, which would
        take the reward list with it — and rewards are typed in by hand with no
        backend to restore them from. Neither half is cosmetic: zustand merges
        persisted state shallowly, so an older `rules` object replaces the
        default wholesale — the dead key would ride along in localStorage
        forever, and the new one would arrive as `undefined` and render as an
        empty number field nobody can save.
      */
      migrate: persisted => {
        const state = persisted as Partial<LoyaltyState> | undefined
        if (state?.rules) {
          delete (state.rules as Partial<LoyaltyRules> & {
            pointsValidityDays?: number
          }).pointsValidityDays
          state.rules = {
            ...DEFAULT_RULES,
            ...state.rules
          }
        }
        /*
          Rewards saved before v4 carry no limit. Left alone they arrive as
          `undefined`, which opens the edit form on an empty required field the
          manager has to fill in before saving an unrelated change.
        */
        if (state?.rewards) {
          state.rewards = state.rewards.map(reward => {
            // Typed as a whole reward, but this is what came out of
            // localStorage, so the new key may simply not be there.
            const stored = reward as Partial<LoyaltyReward>
            return {
              ...reward,
              maxPerRedemption:
                stored.maxPerRedemption ?? DEFAULT_MAX_PER_REDEMPTION
            }
          })
        }
        return state as LoyaltyState
      }
    }
  )
)
